/**
 * Service and inspection policies by equipment type (FE-5; API-9 / 0464).
 *
 * The API's vocabulary, mirrored. `fieldiq-api/src/lib/equipment-service-policy.ts`
 * is the source; the API publishes no endpoint for these lists, and its 400
 * names the allowed values, so a drift shows up as a refused save rather than
 * as a silently wrong row.
 *
 * Decisions (operator, 2026-10-06): D1 Level-1 pre-use types are due at every
 * job start and every other type every 30 days; D4 intervals per type with a
 * per-asset override; D5 left-on-site alert 7 days. These defaults are shown
 * AS defaults — nothing here writes them.
 */
import { inspectionTypeForEquipmentType, type InspectionType } from './equipmentInspectionType';

export type PolicyKind = 'inspection_cadence' | 'service_interval';
export type CadenceTrigger = 'per_job' | 'calendar_days' | 'working_days';

/** Cadence triggers, in the API's order, as the office reads them. */
export const CADENCE_TRIGGERS: { value: CadenceTrigger; label: string }[] = [
  { value: 'per_job', label: 'Every job start' },
  { value: 'calendar_days', label: 'Every N days' },
  { value: 'working_days', label: 'Every N working days' },
];

/**
 * Service-interval triggers. The API also accepts `meters_run`, but its
 * evaluator is a stub that cannot answer (maintenance-schedule.service), so a
 * policy on it would never come due. It is not offered.
 */
export const INTERVAL_TRIGGERS: { value: string; label: string; unit: string }[] = [
  { value: 'calendar_days', label: 'Calendar days', unit: 'days' },
  { value: 'odometer_km', label: 'Odometer (km)', unit: 'km' },
  { value: 'odometer_mi', label: 'Odometer (mi)', unit: 'mi' },
  { value: 'engine_hours', label: 'Engine hours', unit: 'hours' },
  { value: 'job_count', label: 'Jobs', unit: 'jobs' },
];

/** Inspection type → the API's `form_type` slug (production values, 2026-10-07). */
export const INSPECTION_FORM_TYPE: Record<InspectionType, string> = {
  'Tong Truck': 'tong_truck',
  'Power Tong': 'power_tong',
  'CRTi': 'crti',
  'Elevators': 'elevators',
  'Slip Elevator L1': 'slip_elevator_l1',
  'Rotary Slips L1': 'rotary_slips_l1',
};

/** D1: Level-1 pre-use forms are due at every job start. */
const L1_PRE_USE = new Set(['slip_elevator_l1', 'rotary_slips_l1']);
export const DEFAULT_CALENDAR_CADENCE_DAYS = 30;

export function formTypeForEquipmentType(typeName: string | null | undefined): string | null {
  const t = inspectionTypeForEquipmentType(typeName);
  return t ? INSPECTION_FORM_TYPE[t] : null;
}

/**
 * The code default for a type, as a sentence — or null when the type has no
 * inspection form, in which case there is no cadence to default (the API
 * evaluates none). Shown only after the policies have LOADED and are empty.
 */
export function defaultCadenceFor(typeName: string | null | undefined): string | null {
  const form = formTypeForEquipmentType(typeName);
  if (!form) return null;
  return L1_PRE_USE.has(form) ? 'Every job start' : `Every ${DEFAULT_CALENDAR_CADENCE_DAYS} days`;
}

export interface ServicePolicy {
  id: string;
  equipmentTypeId: string;
  equipmentId: string | null;
  kind: PolicyKind;
  triggerType: string;
  intervalValue: number | null;
  formType: string | null;
  name: string;
  isActive: boolean;
}

/**
 * The unit a cadence counts in. A CADENCE ("every 10 working days") is not the
 * cert-day COUNTER ("12 of 500 working days"), whose sentence has one producer
 * (CertDayCounter; pinned by cert-days.containment.test). Naming the unit here
 * keeps the two vocabularies visibly apart rather than looking like a copy.
 */
const CADENCE_UNIT: Record<string, string> = { calendar_days: 'days', working_days: 'working days' };

/** One policy as a phrase: "Every job start", "Every 14 days", "Every 500 km". */
export function describePolicy(p: Pick<ServicePolicy, 'kind' | 'triggerType' | 'intervalValue'>): string {
  if (p.kind === 'inspection_cadence') {
    if (p.triggerType === 'per_job') return 'Every job start';
    if (p.intervalValue == null) return '—';
    return `Every ${p.intervalValue} ${CADENCE_UNIT[p.triggerType] ?? 'days'}`;
  }
  const t = INTERVAL_TRIGGERS.find((x) => x.value === p.triggerType);
  if (p.intervalValue == null) return '—';
  return `Every ${p.intervalValue} ${t?.unit ?? p.triggerType}`;
}

/** The resolver's `source` on a cadence, as the office reads it. */
export const CADENCE_SOURCE_LABELS: Record<string, string> = {
  asset_override: 'set for this asset',
  type_policy: 'from the equipment type',
  default: 'FieldIQ default',
};

/** The body for POST / PUT — snake_case, as the API documents. */
export interface PolicyDraft {
  kind: PolicyKind;
  trigger_type: string;
  interval: string;
  name: string;
}

export function policyDraftError(d: PolicyDraft): string | null {
  if (!d.name.trim()) return 'Give the policy a name';
  if (d.trigger_type === 'per_job') return null;
  const n = Number(d.interval);
  if (!d.interval.trim() || !Number.isFinite(n) || n <= 0) return 'N must be a number greater than 0';
  if (d.kind === 'inspection_cadence' && !Number.isInteger(n)) return 'N must be a whole number of days';
  return null;
}

export function policyBody(d: PolicyDraft, extra: { form_type?: string | null; equipment_id?: string } = {}) {
  return {
    kind: d.kind,
    trigger_type: d.trigger_type,
    interval_value: d.trigger_type === 'per_job' ? null : Number(d.interval),
    name: d.name.trim(),
    ...(d.kind === 'inspection_cadence' && extra.form_type ? { form_type: extra.form_type } : {}),
    ...(extra.equipment_id ? { equipment_id: extra.equipment_id } : {}),
  };
}

/** D5's bounds — companies_maintenance_los_days_chk (0464). */
export const LEFT_ON_SITE_MIN = 1;
export const LEFT_ON_SITE_MAX = 90;

export function leftOnSiteError(text: string): string | null {
  const n = Number(text);
  if (!text.trim() || !Number.isInteger(n) || n < LEFT_ON_SITE_MIN || n > LEFT_ON_SITE_MAX) {
    return `Enter a whole number of days from ${LEFT_ON_SITE_MIN} to ${LEFT_ON_SITE_MAX}`;
  }
  return null;
}

/** POLICY_WRITE_ROLES in equipment-types.routes.ts. */
export const canWriteServicePolicies = (role: string | null | undefined) =>
  role === 'admin' || role === 'office_staff';
